import { useState, useEffect, useCallback } from 'react'
import { api } from '../utils/api'
import { useAuthStore } from '../stores/authStore'
import { useToastStore } from '../stores/toastStore'

export interface AmpDomainState {
  active: string | null
  available: string[]
  envBaseline: string[]
}

// Encapsulates AMP-domain rotation data + mutations. The consuming modal keeps
// only presentation + its own input field state. Behaviour mirrors the previous
// in-component logic exactly (fetch on open, refetch after each mutation).
export function useAmpDomain(isOpen: boolean) {
  const { token, csrfToken } = useAuthStore()
  const { addToast } = useToastStore()
  const [state, setState] = useState<AmpDomainState | null>(null)
  const [loading, setLoading] = useState(false)
  const [busyHostname, setBusyHostname] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const refresh = useCallback(async () => {
    if (!token) return
    try {
      setLoading(true)
      const res = await api.getAmpDomain(token, csrfToken || undefined)
      if (res.success && res.data) {
        setState(res.data)
      } else {
        addToast({ type: 'error', message: res.error || 'Failed to load AMP domain state' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Failed to load AMP domain state' })
    } finally {
      setLoading(false)
    }
  }, [token, csrfToken, addToast])

  useEffect(() => {
    if (isOpen) refresh()
  }, [isOpen, refresh])

  const activate = async (hostname: string) => {
    if (!token) return
    setBusyHostname(hostname)
    try {
      const res = await api.setAmpDomain(hostname, token, csrfToken || undefined)
      if (res.success) {
        addToast({ type: 'success', message: `AMP now served from ${hostname}` })
        await refresh()
      } else {
        addToast({ type: 'error', message: res.error || 'Failed to activate' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Failed to activate' })
    } finally {
      setBusyHostname(null)
    }
  }

  // Returns true on success so the caller can clear its input field.
  const add = async (value: string): Promise<boolean> => {
    if (!value || !token) return false
    setAdding(true)
    try {
      const res = await api.addAmpDomain(value, token, csrfToken || undefined)
      if (res.success) {
        addToast({
          type: 'success',
          message: res.data?.added ? `Added ${res.data.hostname}` : `Already in pool: ${res.data?.hostname}`,
        })
        await refresh()
        return true
      }
      addToast({ type: 'error', message: res.error || 'Failed to add subdomain' })
      return false
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Failed to add subdomain' })
      return false
    } finally {
      setAdding(false)
    }
  }

  const remove = async (hostname: string) => {
    if (!token) return
    setBusyHostname(hostname)
    try {
      const res = await api.removeAmpDomain(hostname, token, csrfToken || undefined)
      if (res.success) {
        addToast({ type: 'success', message: `Removed ${hostname}` })
        await refresh()
      } else {
        addToast({ type: 'error', message: res.error || 'Failed to remove' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Failed to remove' })
    } finally {
      setBusyHostname(null)
    }
  }

  const isEnvBaseline = (hostname: string) => state?.envBaseline.includes(hostname) ?? false

  return { state, loading, busyHostname, adding, activate, add, remove, isEnvBaseline, refresh }
}
